import React, { useState } from 'react'
import { Button, DatePicker, Select } from 'antd'
import dayjs from 'dayjs'
import { SlackOutlined } from '@ant-design/icons'
import { SalesReportAPI, SalesReportDateWiseAPI } from '../../AllAPI/AdminAPI'
import { toast } from 'react-toastify'
import * as XLSX from 'xlsx'
import jsPDF from 'jspdf'
import autoTable from 'jspdf-autotable'
import NotoFont from '../../Assets/Font/NotoSansDevanagari'
import axiosInstance from '../../Utils/axiosInstance'

const { Option } = Select

const SalesReport = () => {
  const selectedYear = Number(sessionStorage.getItem('year'))

  const [loading, setLoading] = useState(false)
  const [viewType, setViewType] = useState('excel')
  const [optionType, setOptionType] = useState('all')
  const [fromDate, setFromDate] = useState(null)
  const [toDate, setToDate] = useState(dayjs())

  /* ---------------- Format Date ---------------- */
  const formatDate = (dateString) => {
    if (!dateString) return ''
    return dayjs(dateString).format('DD-MM-YYYY')
  }

  /* ---------------- Format Sales Data ---------------- */
  const formatSalesData = (data) => {
    const rows = []

    data.forEach((sale) => {
      let firstRow = true

      sale.deliveries?.forEach((delivery) => {
        delivery.products?.forEach((product) => {
          rows.push({
            sales_id: firstRow ? sale.sales_id : '',
            sales_date: firstRow ? formatDate(sale.sales_date) : '',
            customer_name: firstRow ? sale.customer_name : '',
            contact: firstRow ? sale.contact : '',
            address: firstRow ? sale.address : '',
            delivery_id: delivery.delivery_id,
            delivery_date: formatDate(delivery.delivery_date),
            product_name: product.product_name,
            size: product.size,
            quantity: product.quantity,
            rate: product.rate,
            amount: product.quantity * product.rate,
            gross_total: firstRow ? sale.gross_total : '',
            discount_amount: firstRow ? sale.discount_amount : '',
            loading_charges: firstRow ? sale.loading_charges : '',
            net_total: firstRow
              ? Number(sale.gross_total || 0) +
                Number(sale.loading_charges || 0) -
                Number(sale.discount_amount || 0)
              : '',
          })

          firstRow = false
        })
      })
    })

    return rows
  }

  /* ---------------- Fetch Data ---------------- */
  const fetchOrders = async () => {
    try {
      setLoading(true)

      let res

      if (optionType === 'all') {
        res = await axiosInstance.get(`${SalesReportAPI}/${selectedYear}`)
      }

      if (optionType === 'date') {
        if (!fromDate || !toDate) {
          toast.warning('Please select date range')
          return
        }

        res = await axiosInstance.post(SalesReportDateWiseAPI, {
          fromDate: fromDate.format('YYYY-MM-DD'),
          toDate: toDate.format('YYYY-MM-DD'),
        })
      }

      const result = Array.isArray(res?.data) ? res.data : []

      if (!result.length) {
        toast.info('No sales data found')
        return
      }

      if (viewType === 'excel') {
        generateExcel(result)
      } else {
        generatePDF(result)
      }
    } catch (err) {
      console.error('Sales report error:', err)
    } finally {
      setLoading(false)
    }
  }

  /* ---------------- Excel ---------------- */
  const generateExcel = (data, fileName = 'Sales_Report') => {
    const rows = formatSalesData(data)

    //  Format Data (Same Standard Pattern)
    const formattedData = rows.map((item) => ({
      'Sales ID': item.sales_id,
      'Sales Date': item.sales_date,
      Customer: item.customer_name,
      Contact: item.contact,
      Address: item.address,
      'Delivery ID': item.delivery_id,
      'Delivery Date': item.delivery_date,
      Product: item.product_name,
      Size: item.size,
      Quantity: item.quantity,
      Rate: item.rate,
      Amount: item.amount,
      'Gross Total': item.gross_total,
      Discount: item.discount_amount,
      'Loading Charges': item.loading_charges,
      'Net Total': item.net_total,
    }))

    //  Calculate Totals
    const totalQty = formattedData.reduce(
      (sum, item) => sum + Number(item.Quantity || 0),
      0,
    )

    const totalAmount = formattedData.reduce(
      (sum, item) => sum + Number(item.Amount || 0),
      0,
    )

    const totalNet = formattedData.reduce(
      (sum, item) => sum + Number(item['Net Total'] || 0),
      0,
    )

    //  Add Grand Total Row
    formattedData.push({
      'Sales ID': '',
      'Sales Date': '',
      Customer: '',
      Contact: '',
      Address: '',
      'Delivery ID': '',
      'Delivery Date': '',
      Product: '',
      Size: 'Grand Total',
      Quantity: totalQty,
      Rate: '',
      Amount: '',
      'Gross Total': '',
      Discount: '',
      'Loading Charges': '',
      'Net Total': totalNet,
    })

    const worksheet = XLSX.utils.json_to_sheet(formattedData)
    const workbook = XLSX.utils.book_new()
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Sales Report')

    //  Generate Current Date (DD-MM-YYYY)
    const today = new Date()
    const formattedDate = `${String(today.getDate()).padStart(2, '0')}-${String(
      today.getMonth() + 1,
    ).padStart(2, '0')}-${today.getFullYear()}`

    const finalFileName = `${fileName} (${formattedDate}).xlsx`

    //  Download File
    XLSX.writeFile(workbook, finalFileName)
  }

  /* ================= PDF (AutoTable) ================= */
  const generatePDF = (data) => {
    const doc = new jsPDF('landscape', 'mm', 'a4')

    //  Register Marathi Font
    doc.addFileToVFS('NotoSansDevanagari.ttf', NotoFont)
    doc.addFont('NotoSansDevanagari.ttf', 'NotoSans', 'normal')
    doc.setFont('NotoSans')

    const rows = formatSalesData(data)

    const totalQty = rows.reduce(
      (sum, row) => sum + Number(row.quantity || 0),
      0,
    )

    const totalAmount = rows.reduce(
      (sum, row) => sum + Number(row.amount || 0),
      0,
    )

    const totalNet = rows.reduce(
      (sum, row) => sum + Number(row.net_total || 0),
      0,
    )

    const tableColumns = [
      'Sales ID',
      'Sales Date',
      'Customer Name',
      'Contact',
      'Address',
      'Delivery ID',
      'Delivery Date',
      'Product',
      'Size',
      'Qty',
      'Rate',
      'Amount',
      'Gross Total',
      'Loading Charges',
      'Discount',
      'Net Total',
    ]

    const tableRows = rows.map((row) => [
      row.sales_id,
      row.sales_date,
      row.customer_name,
      row.contact,
      row.address,
      row.delivery_id,
      row.delivery_date,
      row.product_name,
      row.size,
      row.quantity,
      row.rate,
      row.amount,
      row.gross_total,
      row.loading_charges,
      row.discount_amount,
      row.net_total,
    ])

    //  Grand Total Row
    tableRows.push([
      '',
      '',
      '',
      '',
      '',
      '',
      '',
      '',
      'Grand Total',
      totalQty,
      '',
      totalAmount,
      '',
      '',
      '',
      totalNet,
    ])

    doc.setFontSize(14)
    doc.text('SALES REPORT', 14, 15)

    const today = new Date().toLocaleDateString('en-GB')

    autoTable(doc, {
      head: [tableColumns],
      body: tableRows,
      startY: 22,
      theme: 'grid',

      //  Full width table
      tableWidth: 'auto',

      styles: {
        font: 'NotoSans',
        fontSize: 8,
        cellPadding: 2,
      },

      headStyles: {
        fillColor: [255, 102, 0],
        font: 'NotoSans',
      },

      margin: { left: 10, right: 10 },

      didDrawPage: function () {
        const pageCount = doc.internal.getNumberOfPages()
        const pageWidth = doc.internal.pageSize.getWidth()
        const pageHeight = doc.internal.pageSize.getHeight()

        doc.setFontSize(9)
        doc.setFont('NotoSans')

        // Page number
        doc.text(
          `Page ${doc.internal.getCurrentPageInfo().pageNumber}`,
          pageWidth - 45,
          pageHeight - 8,
        )

        // Generated date
        doc.text(`(${today})`, pageWidth - 10, pageHeight - 8, {
          align: 'right',
        })
      },
    })

    const blob = doc.output('blob')
    window.open(URL.createObjectURL(blob), '_blank')
  }

  return (
    <div className="mainContainer">
      {loading && (
        <div className="fullscreen-loader">
          <div className="loader-box">
            <div className="spinner"></div>
            <p>Generating Report...</p>
          </div>
        </div>
      )}

      <div className="header-raw">
        <h3>
          <SlackOutlined style={{ color: 'orangered' }} /> Sales Report
        </h3>
      </div>

      <div className="row p-2 m-0 mb-2 rounded border shadow">
        <div className="col-md-2 pt-2">
          <label>View Type</label>
          <Select
            style={{ width: '100%' }}
            value={viewType}
            onChange={(value) => setViewType(value)}
          >
            <Option value="excel">Excel</Option>
            <Option value="pdf">PDF</Option>
          </Select>
        </div>

        <div className="col-md-2 pt-2">
          <label>Options</label>
          <Select
            style={{ width: '100%' }}
            value={optionType}
            onChange={(value) => setOptionType(value)}
          >
            <Option value="all">All</Option>
            <Option value="date">Date Wise</Option>
          </Select>
        </div>

        {optionType === 'date' && (
          <>
            <div className="col-md-2 pt-2">
              <label>From Date</label>
              <DatePicker
                value={fromDate}
                onChange={setFromDate}
                format="DD-MM-YYYY"
              />
            </div>

            <div className="col-md-2 pt-2">
              <label>To Date</label>
              <DatePicker
                value={toDate}
                onChange={setToDate}
                format="DD-MM-YYYY"
              />
            </div>
          </>
        )}

        <div className="col-md-2 pt-2 d-flex align-items-end">
          <Button
            type="primary"
            className="rounded"
            loading={loading}
            onClick={fetchOrders}
          >
            Show
          </Button>
        </div>
      </div>
    </div>
  )
}

export default SalesReport
